import React, { useCallback, useEffect } from 'react';

import { CompletionStatus } from '@eika-felles/kyc-declaration-lib/dist/generated-types';

import {
    fetchApplication,
    FormTypeCompletionDetails,
    getOrCreateSblConsent,
    insuranceClickInc,
    kycError,
    KycErrorAction,
    loanCompletionDetailsNextEvent,
    openStep,
    sblFailed,
    setKycAnswered,
    skipKyc,
    updateApplicationKycNextEvent,
    UpdateConsent,
    updateConsent,
    updateStepsCompletionFetch,
} from '../../actions';
import { DispatchWithState } from '../../actions/types';
import Loader from '../../components/Loader/Loader';
import ExpandableTableItems from '../../components/LoanApplication/ExpandableTableItems';
import ProductImage from '../../components/ProductImage/ProductImage';
import Step from '../../components/Steps/Step';
import StepCounter from '../../components/Steps/StepCounter';
import Steps from '../../components/Steps/Steps';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
// reducer types removed; using selectors with inferred types
import { getProductCompletionTitle, getProductIcon } from '../../util/mappings';
import { trackEvent, TrackingStep } from '../../util/piwik-utils';
import LoanCompletionDetails from './LoanCompletionDetails/LoanCompletionDetails';
import LoanCompletionKyc from './LoanCompletionKyc/LoanCompletionKyc';
import LoanCompletionReceipt from './LoanCompletionReceipt/LoanCompletionReceipt';
import LoanCompletionSbl from './LoanCompletionSbl/LoanCompletionSbl';
import LoanCompletionSigning from './LoanCompletionSigning/LoanCompletionSigning';
import LoanCompletionStart from './LoanCompletionStart/LoanCompletionStart';

const SkipStep = ({ action }: { action: any }) => {
    useEffect(() => {
        action.openStep(4);
    }, [action]);
    return <></>;
};

const LoanCompletion: React.FC = () => {
    const dispatch = useAppDispatch();

    const text = useAppSelector((s) => s.context.config.data.text);
    const application = useAppSelector((s) => s.application);
    const completion = useAppSelector((s) => s.completion);
    const calculator = useAppSelector((s) => s.calculator);
    const consent = useAppSelector((s) => s.consent);
    const context = useAppSelector((s) => s.context);
    const vehicle = useAppSelector((s) => s.vehicle);
    const loader = useAppSelector((s) => s.loader);
    const steps = useAppSelector((s) => s.steps);
    const user = useAppSelector((s) => s.user);
    const sbl = useAppSelector((s) => s.sbl);
    const feature = useAppSelector((s) => s.feature);

    useEffect(() => {
        if (!loader.hasFetchedApplication && !loader.isFetchingApplication && !loader.fetchApplicationFailed) {
            dispatch(fetchApplication() as unknown as DispatchWithState);
        }
        dispatch(updateStepsCompletionFetch() as unknown as DispatchWithState);
        // we intentionally omit dispatch from deps to avoid re-running on identity change
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loader.hasFetchedApplication, loader.isFetchingApplication, loader.fetchApplicationFailed]);

    // Stable reference so the signing progress polling effect isn't torn down/re-run on every render.
    const refreshApplication = useCallback(() => {
        // Consent is only needed for the consent page (fetched during the initial load); skip it while
        // polling on the signing step to avoid the redundant alternating consent/ongoing-application requests.
        dispatch(fetchApplication({ skipConsent: true }) as unknown as DispatchWithState);
    }, [dispatch]);

    const action = {
        fetchApplication: (): DispatchWithState =>
            dispatch(fetchApplication() as unknown as DispatchWithState) as unknown as DispatchWithState,
        loanCompletionDetailsNextEvent: (form: FormTypeCompletionDetails): DispatchWithState =>
            dispatch(
                loanCompletionDetailsNextEvent(form) as unknown as DispatchWithState,
            ) as unknown as DispatchWithState,
        updateConsent: (name: UpdateConsent['name'], value: UpdateConsent['value']): void => {
            dispatch(updateConsent(name, value));
        },
        updateApplicationKycNextEvent: (): DispatchWithState =>
            dispatch(updateApplicationKycNextEvent() as unknown as DispatchWithState) as unknown as DispatchWithState,
        openStep: (step: number): void => {
            dispatch(openStep(step));
        },
        setKycAnswered: (): void => {
            dispatch(setKycAnswered());
        },
        kycError: (): KycErrorAction => dispatch(kycError()),
        skipKyc: (): void => {
            dispatch(skipKyc());
        },
        insuranceClickInc: (): void => {
            // insuranceClickInc is typed to return void; invoke directly (no dispatch)
            insuranceClickInc();
        },
        getOrCreateSblConsent: (): DispatchWithState =>
            dispatch(getOrCreateSblConsent() as unknown as DispatchWithState) as unknown as DispatchWithState,
        sblFailed: (): void => {
            dispatch(sblFailed());
        },
        updateStepsCompletionFetch: (): DispatchWithState =>
            dispatch(updateStepsCompletionFetch() as unknown as DispatchWithState) as unknown as DispatchWithState,
    };

    const { isFetchingApplication, hasFetchedApplication, hasNotYetUpdatedCompletionSteps } = loader;
    const { applicationErrorNotFound } = application;
    const { loanInfo } = calculator;
    const { loanType, loanProduct } = loanInfo;
    const { currentStep } = steps;
    const loanProductTitle = getProductCompletionTitle(loanType, loanProduct, text);
    // Consent page is gated by an Unleash toggle; when salgspant-skip-consent is enabled (or there are no consents) it is skipped.
    const showConsentPage = !feature.salgspantSkipConsent && consent.data.length > 0;
    const kycBackButtonStep = !showConsentPage && completion.ongoing.customerIsMainApplicant ? 1 : 4;

    if ((isFetchingApplication && !hasFetchedApplication) || hasNotYetUpdatedCompletionSteps) {
        return <Loader title={text.loadingApplicationStatus} />;
    }

    return (
        <div className="loan-completion">
            <div style={{ maxWidth: '800px', margin: '0 auto' }}>
                <>
                    {currentStep === 1 ? (
                        <ProductImage
                            className="loan-completion__intro"
                            image={getProductIcon(loanType, loanProduct)}
                            title={loanProductTitle && loanProductTitle}
                        />
                    ) : (
                        <>{loanProductTitle && <h1 className="text-center">{loanProductTitle}</h1>}</>
                    )}

                    {hasFetchedApplication ? (
                        <>
                            <Steps>
                                <Step open={currentStep === 1}>
                                    <StepCounter currentStep="1" totalSteps="7" />
                                    <LoanCompletionStart
                                        action={action}
                                        completion={completion}
                                        calculator={calculator}
                                        vehicle={vehicle}
                                        feature={feature}
                                        text={text}
                                        sbl={sbl}
                                    />
                                </Step>
                                <Step open={currentStep === 2}>
                                    <StepCounter currentStep="2" totalSteps="7" />
                                    <LoanCompletionSbl
                                        action={action}
                                        loader={loader}
                                        text={text}
                                        sbl={sbl}
                                        completion={completion}
                                    />
                                </Step>

                                <Step open={currentStep === 3}>
                                    <SkipStep action={action} />
                                </Step>

                                <Step open={currentStep === 4}>
                                    <StepCounter currentStep="4" totalSteps="7" />
                                    <LoanCompletionDetails
                                        action={action}
                                        completion={completion}
                                        calculator={calculator}
                                        vehicle={vehicle}
                                        text={text}
                                        consent={consent}
                                        showConsentPage={showConsentPage}
                                    />
                                </Step>
                                <Step open={currentStep === 5}>
                                    <StepCounter currentStep="5" totalSteps="7" />
                                    <LoanCompletionKyc
                                        ssn={user.context.ssn}
                                        previousEvent={() => action.openStep(kycBackButtonStep)}
                                        completeEvent={(status: string) => {
                                            trackEvent(TrackingStep.CompletionKyc);
                                            if (status === CompletionStatus.FAILED) action.kycError();
                                            else if (
                                                status === CompletionStatus.OK ||
                                                status === CompletionStatus.REQUIRES_MANUAL_CONTROL_BY_ADVISOR
                                            ) {
                                                action.setKycAnswered();
                                                action.updateApplicationKycNextEvent();
                                            }
                                        }}
                                        skipEvent={() => {
                                            action.skipKyc();
                                            action.updateApplicationKycNextEvent();
                                        }}
                                        skipKycEnabled={feature.salgspantSkipKyc}
                                        text={text}
                                        loader={loader}
                                    />
                                </Step>

                                <Step open={currentStep === 6}>
                                    <StepCounter currentStep="6" totalSteps="7" />
                                    <LoanCompletionSigning
                                        context={context}
                                        completion={completion}
                                        feature={feature}
                                        refreshApplication={refreshApplication}
                                    />
                                </Step>

                                <Step open={currentStep === 7}>
                                    <StepCounter currentStep="7" totalSteps="7" />
                                    <LoanCompletionReceipt
                                        text={text}
                                        completion={completion}
                                        calculator={calculator}
                                        isEika={context.config.bankregnr === '0770'}
                                        insuranceClick={action.insuranceClickInc}
                                    />
                                </Step>
                            </Steps>
                        </>
                    ) : (
                        <>
                            {applicationErrorNotFound ? (
                                <p>{text.completionApplicationNotFound}</p>
                            ) : (
                                <p>{text.completionApplicationMissingCaseId}</p>
                            )}
                        </>
                    )}

                    <ExpandableTableItems text={text} />
                </>
            </div>
        </div>
    );
};

export default LoanCompletion;
